import React, { useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { HelpCircle, Moon, Sun, CalendarSync, CalendarDays, Shuffle, Star } from 'lucide-react';
import { useTheme } from 'next-themes';
import { GameMode } from '@/utils/gameState';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import { useGame } from '@/context/GameContext';

interface NavbarProps {
  onHelpClick: () => void;
  gameMode: GameMode;
  onSelectGameMode: (mode: GameMode) => void;
  canPlayEncore: boolean;
}

const Navbar: React.FC<NavbarProps> = ({ 
  onHelpClick, 
  onSelectGameMode,
  canPlayEncore
}) => {
  const { resolvedTheme, setTheme } = useTheme();
  const { gameState, setShowArchive, gameConfig } = useGame();
  const toggleTheme = () => {
    setTheme(resolvedTheme === 'dark' ? 'light' : 'dark');
  };

  // Show loading state while theme is being determined
  const isDarkMode = resolvedTheme === 'dark';
  
  // Define theme-dependent styles
  const navbarGradient = isDarkMode 
    ? "from-fourdle-purple-light to-fourdle-purple-dark" 
    : "from-fourdle-yellow-light to-fourdle-yellow-dark";

  const logoTextColor = isDarkMode ? "text-fourdle-yellow" : "text-fourdle-purple";
  
  // Get the current game mode icon and name
  const getModeDetails = useCallback(() => {
    switch (gameConfig.mode) {
      case GameMode.ENCORE:
        return { icon: <Star className="h-5 w-5" />, name: 'Encore' };
      case GameMode.DAILY:
        return { icon: <CalendarSync className="h-5 w-5" />, name: 'Daily' };
      case GameMode.ARCHIVE:
        return { icon: <CalendarDays className="h-5 w-5" />, name: gameState.targetWordNumber?.toLocaleString() || 'Archive' };
      case GameMode.RANDOM:
      default:
        return { icon: <Shuffle className="h-5 w-5" />, name: 'Random' };
    }
  }, [gameConfig.mode, gameState.targetWordNumber]);

  const { icon: currentModeIcon, name: currentModeName } = getModeDetails();
  
  return (
    <header className={`flex items-center justify-between pt-safe-or-[theme(spacing.3)] py-3 px-4 bg-gradient-to-r ${navbarGradient} ${isDarkMode ? 'text-white' : 'text-black'}`}>
      <div className="flex-none flex items-center justify-start space-x-2">
        <img 
          src="/logo-transparent-512x512.png" 
          alt="Fourdle Logo" 
          className="h-8 w-8"
        />
        <h1 className="text-2xl font-logo font-bold tracking-wide">
          <span className={logoTextColor}>Fourdle</span>
        </h1>
      </div>
      
      <div className="flex-1 flex items-center justify-end space-x-1">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button 
              variant="ghost" 
              size="icon" 
              aria-label="Select game mode"
              title="Select game mode"
              className={`w-auto items-center border-[1px] px-3 items-center ${isDarkMode ? 'text-white border-white/10 hover:bg-white/10' : 'text-black border-black/10 hover:bg-black/10'}`}
            >
              {currentModeIcon}
              <span className="text-sm">{currentModeName}</span>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem 
              onClick={() => onSelectGameMode(GameMode.DAILY)} 
              className={`flex items-center gap-2 ${gameConfig.mode === GameMode.DAILY ? 'bg-accent' : ''}`}
            >
              <CalendarSync className="h-4 w-4" />
              <span>Daily</span>
            </DropdownMenuItem>
            <DropdownMenuItem 
              onClick={() => setShowArchive(true)} 
              className={`flex items-center gap-2 ${gameConfig.mode === GameMode.ARCHIVE ? 'bg-accent' : ''}`}
            >
              <CalendarDays className="h-4 w-4" />
              <span>Archive</span>
            </DropdownMenuItem>
            <DropdownMenuItem 
              onClick={() => onSelectGameMode(GameMode.RANDOM)} 
              className={`flex items-center gap-2 ${gameConfig.mode === GameMode.RANDOM ? 'bg-accent' : ''}`}
            >
              <Shuffle className="h-4 w-4" />
              <span>Random</span>
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem 
              onClick={() => canPlayEncore && onSelectGameMode(GameMode.ENCORE)}
              disabled={!canPlayEncore}
              className={`flex items-center gap-2 ${gameConfig.mode === GameMode.ENCORE ? 'bg-accent' : ''}`}
            >
              <Star className="h-4 w-4" />
              <span>Encore</span>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
        
        <Button 
          variant="ghost" 
          size="icon" 
          onClick={toggleTheme} 
          aria-label="Toggle theme"
          className={`${isDarkMode ? 'text-white hover:bg-white/10' : 'text-black hover:bg-black/10'}`}
        >
          {isDarkMode ? (
            <Sun className="h-5 w-5" />
          ) : (
            <Moon className="h-5 w-5" />
          )}
        </Button>
        <Button 
          variant="ghost" 
          size="icon" 
          onClick={onHelpClick} 
          aria-label="Help and Info"
          className={`${isDarkMode ? 'text-white hover:bg-white/10' : 'text-black hover:bg-black/10'}`}
        >
          <HelpCircle className="h-5 w-5" />
        </Button>
      </div>
    </header>
  );
};

export default Navbar;
